import React, { useCallback, useMemo, useRef } from "react";
import {
  Bot, ClipboardList, Bell, Settings,
  BarChart2, DollarSign, ShieldCheck, Cpu, Network, LayoutDashboard, HardDrive,
} from "lucide-react";
import { ROUTES_REGISTRY, isRouteVisible, RouteEntry } from "../../config/routes.registry";
import { useEffectiveRole } from "../../hooks/useEffectiveRole";
import useAuthorizedModules from "../../hooks/useAuthorizedModules";

type LucideLike = React.FC<{ size?: number; strokeWidth?: number }>;

export interface MobileNavItem {
  key: string;
  to: string;
  label: string;
  category: string;
  Icon?: LucideLike;
  emoji?: string;
}

/**
 * Curated shortcuts with short labels and line icons. Keys are persisted in
 * the user's workspace preferences, so they must never be renamed.
 */
export const NAV_REGISTRY: Record<string, { to: string; label: string; Icon: LucideLike }> = {
  clu:            { to: "/m/clu-ng",            label: "CLU",      Icon: Bot },
  jobs:           { to: "/m/jobs",              label: "Jobs",     Icon: ClipboardList },
  notifications:  { to: "/m/notifications",     label: "Notifs",   Icon: Bell },
  settings:       { to: "/m/settings",          label: "Settings", Icon: Settings },
  workspace:      { to: "/m/settings/my-workspace", label: "Workspace", Icon: LayoutDashboard },
  financial:      { to: "/m/settings/financial-intelligence", label: "Finance", Icon: DollarSign },
  monitoring:     { to: "/m/settings/system-health", label: "Monitor", Icon: BarChart2 },
  compliance:     { to: "/m/settings/compliance", label: "Comply", Icon: ShieldCheck },
  smartagents:    { to: "/m/smartagents",       label: "Agents",   Icon: Cpu },
  navigator:      { to: "/m/navigator-admin",   label: "RPIA",     Icon: Network },
  drive:          { to: "/m/drive",             label: "Drive",    Icon: HardDrive },
};

export const DEFAULT_NAV_ORDER = ["clu", "jobs", "notifications", "workspace"];

export const MAX_TAB_SLOTS = 4;

export const TAB_CONTEXT = "mobile:nav";
export const FAVORITES_CONTEXT = "mobile:favorites";

const ROUTE_KEY_PREFIX = "route:";
const QUICK_CATEGORY = "Quick access";

export function routeKey(mobilePath: string): string {
  return `${ROUTE_KEY_PREFIX}${mobilePath}`;
}

type ModuleCheck = (code: string | undefined) => boolean;

function isAdminRole(role: string): boolean {
  return role === "admin" || role === "super_user";
}

function fromRoute(route: RouteEntry): MobileNavItem {
  return {
    key: routeKey(route.mobilePath),
    to: route.mobilePath,
    label: route.label,
    category: route.category,
    emoji: route.icon,
  };
}

export function resolveNavItem(key: string, role: string, checkModule?: ModuleCheck): MobileNavItem | null {
  const curated = NAV_REGISTRY[key];
  if (curated) {
    if (key === "settings" && !isAdminRole(role)) return null;
    return { key, ...curated, category: QUICK_CATEGORY };
  }
  if (!key.startsWith(ROUTE_KEY_PREFIX)) return null;
  const mobilePath = key.slice(ROUTE_KEY_PREFIX.length);
  const route = ROUTES_REGISTRY.find((r) => r.mobilePath === mobilePath);
  if (!route || !isRouteVisible(route, role, checkModule)) return null;
  return fromRoute(route);
}

/**
 * Every page the user may pin: the curated shortcuts first, then each mobile
 * route of ROUTES_REGISTRY the user can see (same Zero Trust filter as the
 * Command Palette), one entry per destination.
 */
export function getPinnableItems(role: string, checkModule?: ModuleCheck): MobileNavItem[] {
  const items: MobileNavItem[] = [];
  const seen = new Set<string>();
  for (const key of Object.keys(NAV_REGISTRY)) {
    const item = resolveNavItem(key, role, checkModule);
    if (!item || seen.has(item.to)) continue;
    seen.add(item.to);
    items.push(item);
  }
  for (const route of ROUTES_REGISTRY) {
    if (!route.mobilePath.startsWith("/m/") || seen.has(route.mobilePath)) continue;
    if (!isRouteVisible(route, role, checkModule)) continue;
    seen.add(route.mobilePath);
    items.push(fromRoute(route));
  }
  return items;
}

/** Finds the pinnable entry whose destination is the given pathname. */
export function findItemForPath(items: MobileNavItem[], pathname: string): MobileNavItem | null {
  const clean = pathname.replace(/\/+$/, "");
  return items.find((item) => item.to === clean) ?? null;
}

export function useMobileNavCatalog() {
  const { effectiveRole } = useEffectiveRole();
  const { isModuleAuthorized } = useAuthorizedModules();
  const checkRef = useRef<ModuleCheck>(isModuleAuthorized);
  checkRef.current = isModuleAuthorized;
  const checkModule = useCallback((code: string | undefined) => checkRef.current(code), []);
  const role = effectiveRole || "user";

  const pinnable = useMemo(() => getPinnableItems(role, checkModule), [role, checkModule, isModuleAuthorized]);
  const resolve = useCallback((key: string) => resolveNavItem(key, role, checkModule), [role, checkModule, isModuleAuthorized]);

  return { pinnable, resolve };
}

export function NavItemIcon({ item, size = 18, strokeWidth = 1.8 }: { item: MobileNavItem; size?: number; strokeWidth?: number }) {
  if (item.Icon) {
    const { Icon } = item;
    return <Icon size={size} strokeWidth={strokeWidth} />;
  }
  return (
    <span aria-hidden="true" style={{ fontSize: size, lineHeight: 1 }}>
      {item.emoji}
    </span>
  );
}
